/**
 * Performance Test Page
 *
 * Measures:
 * 1. End-to-end latency of a single MsgPlaceOrderRequest transaction (in ms).
 * 2. Throughput (TPS) by sending a configurable burst of MsgPlaceOrderRequest
 *    transactions and measuring how many succeed per second.
 *
 * Follows the same onboarding and FiClient patterns as SDKTestPage.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { InfoClient, toSatoshi, fromSatoshi } from "../sdk";
import { SubscriptionProvider } from "../context/SubscriptionContext";
import { useFiClient } from "../hooks/useFiClient";
import { useOnboard } from "../hooks/useOnboard";
import { useMarkets } from "../hooks/useMarkets";
import type { Market } from "../store/types";
import type { RawBucket } from "../sdk";
import { DEFAULT_BASE_URL, DEFAULT_WS_URL } from "../constants";
import { DEFAULT_MNEMONIC } from "../constants";
import "./performance_test_page/perftest.css";

// ============================================================================
// Types
// ============================================================================

interface LogEntry {
  level: "info" | "success" | "error";
  message: string;
  timestamp: number;
}

interface LatencyResult {
  latencyMs: number;
  success: boolean;
  txHash?: string;
  orderId?: string;
  error?: string;
}

interface TpsResult {
  totalSent: number;
  totalSucceeded: number;
  totalFailed: number;
  durationMs: number;
  tps: number;
  avgLatencyMs: number;
  minLatencyMs: number;
  maxLatencyMs: number;
  perTxResults: LatencyResult[];
}

// ============================================================================
// Helpers
// ============================================================================

/** Resolve the first active market ID from a market list. */
function getFirstMarketId(list: Market[]): string {
  if (!list || list.length === 0) return "";
  const m = list.find((x) => x.active) ?? list[0];
  return m.index ?? m.marketIndex ?? "";
}

/** Resolve the first bucket ID for a wallet via InfoClient. */
async function resolveFirstBucketId(
  infoClient: InfoClient,
  walletAddress: string,
): Promise<string> {
  const buckets = await infoClient.getBuckets(walletAddress);
  const list: RawBucket[] = Array.isArray(buckets) ? buckets : [];
  if (list.length === 0) return "";
  return list[0].bucketId ?? "";
}

/** Fetch the current market price (decimal) via InfoClient. */
async function fetchCurrentPrice(
  infoClient: InfoClient,
  marketId: string,
): Promise<number> {
  const data = await infoClient.getMarketData(marketId);
  const raw = data as Record<string, unknown>;
  const lp =
    raw.lastPrice ?? raw.last_price ?? raw.markPrice ?? raw.mark_price;
  const price = lp ? fromSatoshi(String(lp)) : 0;
  return price > 0 ? price : 50_000; // fallback
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_TPS_TX_COUNT = 50;
const MIN_TPS_TX_COUNT = 5;
const MAX_TPS_TX_COUNT = 10000;
const DEFAULT_LEVERAGE = 10;

// ============================================================================
// Component
// ============================================================================

function PerformanceTestPageContent() {
  // --- Config state ---
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const [tpsTxCount, setTpsTxCount] = useState(DEFAULT_TPS_TX_COUNT);

  // --- Result state ---
  const [latencyResult, setLatencyResult] = useState<LatencyResult | null>(null);
  const [tpsResult, setTpsResult] = useState<TpsResult | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);

  // --- Running state ---
  const [isLatencyRunning, setIsLatencyRunning] = useState(false);
  const [isTpsRunning, setIsTpsRunning] = useState(false);
  const [tpsProgress, setTpsProgress] = useState<string>("");

  // --- Refs ---
  const infoClientRef = useRef<InfoClient | null>(null);
  const cancelledRef = useRef(false);

  // --- Hooks ---
  const {
    mnemonic,
    walletAddress,
    authToken,
    loading: onboardLoading,
    error: onboardError,
    onboard: performOnboard,
    clearError: clearOnboardError,
    setMnemonic: setOnboardMnemonic,
  } = useOnboard(baseURL);

  const { client: exchangeClient, isReady: exchangeReady } = useFiClient({
    baseURL,
    mnemonic: mnemonic ?? "",
  });

  const { markets, fetchMarkets } = useMarkets({
    baseURL,
    sessionToken: authToken,
    autoFetchMarkets: true,
  });

  // Initialize default mnemonic
  useEffect(() => {
    if (!mnemonic) {
      setOnboardMnemonic(DEFAULT_MNEMONIC);
    }
  }, [mnemonic, setOnboardMnemonic]);

  // --- Logging ---
  const addLog = useCallback(
    (level: LogEntry["level"], message: string) => {
      setLogs((prev) => [...prev, { level, message, timestamp: Date.now() }]);
    },
    [],
  );

  const clearLogs = useCallback(() => {
    setLogs([]);
    setLatencyResult(null);
    setTpsResult(null);
    setTpsProgress("");
  }, []);

  // --- Prerequisites: resolve marketId, bucketId, price ---
  const resolvePrerequisites = useCallback(async (): Promise<{
    marketId: string;
    bucketId: string;
    price: number;
  } | null> => {
    if (!exchangeClient || !exchangeReady) {
      addLog("error", "FiClient not ready. Set mnemonic and base URL first.");
      return null;
    }
    if (!walletAddress) {
      addLog("error", "Wallet address unavailable. Please onboard first.");
      return null;
    }

    // InfoClient
    if (!infoClientRef.current) {
      infoClientRef.current = new InfoClient({ baseURL });
    }
    const infoClient = infoClientRef.current;

    // Market
    let marketId = getFirstMarketId(markets);
    if (!marketId) {
      addLog("info", "No markets loaded; fetching...");
      const fetched = await fetchMarkets();
      const list = Array.isArray(fetched) ? fetched : [];
      marketId = getFirstMarketId(list);
    }
    if (!marketId) {
      addLog("error", "No active market found.");
      return null;
    }

    // Bucket
    const bucketId = await resolveFirstBucketId(infoClient, walletAddress);
    if (!bucketId) {
      addLog("error", "No bucket found for wallet. Please create a bucket first.");
      return null;
    }

    // Price
    const price = await fetchCurrentPrice(infoClient, marketId);

    // Refresh nonce before write transactions
    await exchangeClient.refreshNonceFromServer();

    addLog("info", `Market: ${marketId.substring(0, 16)}… | Bucket: ${bucketId.substring(0, 16)}… | Price: $${price.toFixed(2)}`);
    return { marketId, bucketId, price };
  }, [exchangeClient, exchangeReady, walletAddress, baseURL, markets, fetchMarkets, addLog]);

  // --- Latency Test ---
  const runLatencyTest = useCallback(async () => {
    setIsLatencyRunning(true);
    setLatencyResult(null);
    addLog("info", "Starting latency test — single MsgPlaceOrderRequest…");

    try {
      const prereqs = await resolvePrerequisites();
      if (!prereqs) {
        setIsLatencyRunning(false);
        return;
      }
      const { marketId, bucketId, price } = prereqs;
      const leverageStr = toSatoshi(String(DEFAULT_LEVERAGE));

      const start = performance.now();
      const result = await exchangeClient!.placeOrder({
        marketId,
        bucketId,
        side: "buy",
        orderType: "LIMIT",
        price: price * 0.95, // limit below current price so it rests on book
        quantity: 0.001,
        timeInForce: "GTC",
        leverage: leverageStr,
      });
      const elapsed = performance.now() - start;

      const latency: LatencyResult = {
        latencyMs: Math.round(elapsed * 100) / 100,
        success: result?.success === true,
        txhash: result?.txhash,
        orderId: result?.orderId,
        error: result?.success ? undefined : (result as unknown as Record<string, unknown>)?.errorMessage as string | undefined,
      };

      setLatencyResult(latency);
      if (latency.success) {
        addLog("success", `Latency: ${latency.latencyMs} ms | txhash: ${latency.txhash ?? "—"}`);
      } else {
        addLog("error", `Order rejected (${latency.latencyMs} ms): ${latency.error ?? "unknown error"}`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setLatencyResult({ latencyMs: 0, success: false, error: msg });
      addLog("error", `Latency test failed: ${msg}`);
    } finally {
      setIsLatencyRunning(false);
    }
  }, [exchangeClient, resolvePrerequisites, addLog]);

  // --- TPS Test ---
  // Two-phase design:
  //   Phase 1 (Signing): Build and sign all requests serially (WASM is single-threaded).
  //            Yields to the event loop periodically to keep the UI responsive.
  //   Phase 2 (Submission): Fire all pre-signed HTTP requests concurrently.
  //            Per-tx latency measures only network + server processing time.
  //
  // This separation prevents the page from freezing during large bursts and
  // provides accurate per-tx latency that reflects the server's capability.
  const runTpsTest = useCallback(async () => {
    setIsTpsRunning(true);
    setTpsResult(null);
    setTpsProgress("");
    cancelledRef.current = false;

    const count = Math.max(MIN_TPS_TX_COUNT, Math.min(tpsTxCount, MAX_TPS_TX_COUNT));
    addLog("info", `Starting TPS test — sending ${count} MsgPlaceOrderRequest transactions…`);

    try {
      const prereqs = await resolvePrerequisites();
      if (!prereqs) {
        setIsTpsRunning(false);
        return;
      }
      const { marketId, bucketId, price } = prereqs;
      const leverageStr = toSatoshi(String(DEFAULT_LEVERAGE));

      // Pre-fetch all nonces in a single batch round-trip.
      if (!infoClientRef.current) {
        infoClientRef.current = new InfoClient({ baseURL });
      }
      const batchNonces = await infoClientRef.current.getNonceBatch(
        walletAddress!,
        count,
      );
      if (batchNonces.length < count) {
        addLog("error", `Batch nonce reservation returned ${batchNonces.length}/${count} nonces. Proceeding with ${batchNonces.length}.`);
        if (batchNonces.length === 0) {
          setIsTpsRunning(false);
          return;
        }
      }
      const actualCount = batchNonces.length;
      addLog("info", `Reserved ${actualCount} nonces [${batchNonces[0]}..${batchNonces[actualCount - 1]}]`);

      // ---- Phase 1: Pre-sign all requests (serial, yields every SIGN_YIELD_INTERVAL) ----
      const SIGN_YIELD_INTERVAL = 20; // yield to event loop every N signatures
      const signedRequests: { txRequest: Record<string, unknown>; idx: number }[] = [];
      const signStart = performance.now();
      setTpsProgress(`Signing: 0 / ${actualCount}`);

      for (let i = 0; i < actualCount; i++) {
        const orderPrice = price * (0.90 + (i % 10) * 0.01);
        const txRequest = await exchangeClient!.buildSignedOrderRequest(
          {
            marketId,
            bucketId,
            side: i % 2 === 0 ? "buy" : "sell",
            orderType: "LIMIT",
            price: orderPrice,
            quantity: 0.001,
            timeInForce: "GTC",
            leverage: leverageStr,
          },
          batchNonces[i],
        );
        signedRequests.push({ txRequest, idx: i });

        // Yield to event loop periodically to keep UI responsive
        if ((i + 1) % SIGN_YIELD_INTERVAL === 0 || i === actualCount - 1) {
          setTpsProgress(`Signing: ${i + 1} / ${actualCount}`);
          await new Promise((r) => setTimeout(r, 0));
        }
      }

      const signDuration = performance.now() - signStart;
      addLog("info", `Phase 1 (signing): ${actualCount} txs in ${signDuration.toFixed(1)} ms (${(signDuration / actualCount).toFixed(1)} ms/tx)`);

      // ---- Phase 2: Batch submit all pre-signed requests in a single HTTP request ----
      //
      // Why batch? Browser HTTP/1.1 limits concurrent connections to ~6 per host.
      // Sending N individual requests causes severe head-of-line blocking:
      //   - With 1000 requests, only 6 run in parallel; the rest queue.
      //   - Measured per-tx latency = queue_wait + server_time, not just server_time.
      //   - This inflates average latency from ~20ms to ~250ms+.
      //
      // Batch submission sends all transactions in ONE HTTP POST to /tx/v1/submit/batch.
      // The router forwards the entire batch to morphcore in a single gRPC call.
      // Morphcore processes all transactions concurrently via its pipeline semaphore.
      // The amortized per-tx latency = total_batch_time / tx_count.
      const perTxResults: LatencyResult[] = [];
      const overallStart = performance.now();
      setTpsProgress(`Submitting batch: ${actualCount} txs…`);

      try {
        const batchResults = await exchangeClient!.submitBatchSignedOrderRequests(
          signedRequests.map((sr) => sr.txRequest),
        );

        const batchDuration = performance.now() - overallStart;
        const amortizedLatency = batchDuration / actualCount;

        // Map batch results to per-tx results with amortized latency
        for (let i = 0; i < batchResults.length; i++) {
          const r = batchResults[i];
          perTxResults.push({
            latencyMs: Math.round(amortizedLatency * 100) / 100,
            success: r.success,
            txhash: r.txhash,
            orderId: r.orderId,
            error: r.success ? undefined : r.errorMessage || undefined,
          });
        }
        // If batch returned fewer results than sent (shouldn't happen, but be defensive)
        for (let i = batchResults.length; i < actualCount; i++) {
          perTxResults.push({
            latencyMs: Math.round(amortizedLatency * 100) / 100,
            success: false,
            error: "no result returned for this transaction",
          });
        }
      } catch (err: unknown) {
        const batchDuration = performance.now() - overallStart;
        const msg = err instanceof Error ? err.message : String(err);
        // All transactions failed
        for (let i = 0; i < actualCount; i++) {
          perTxResults.push({
            latencyMs: Math.round((batchDuration / actualCount) * 100) / 100,
            success: false,
            error: msg,
          });
        }
      }

      const overallDuration = performance.now() - overallStart;

      const succeeded = perTxResults.filter((r) => r.success);
      const failed = perTxResults.filter((r) => !r.success);
      const latencies = perTxResults.map((r) => r.latencyMs);

      const tps =
        overallDuration > 0
          ? (succeeded.length / overallDuration) * 1000
          : 0;
      const avgLatency =
        latencies.length > 0
          ? latencies.reduce((a, b) => a + b, 0) / latencies.length
          : 0;

      const result: TpsResult = {
        totalSent: actualCount,
        totalSucceeded: succeeded.length,
        totalFailed: failed.length,
        durationMs: Math.round(overallDuration * 100) / 100,
        tps: Math.round(tps * 100) / 100,
        avgLatencyMs: Math.round(avgLatency * 100) / 100,
        minLatencyMs:
          latencies.length > 0
            ? Math.round(Math.min(...latencies) * 100) / 100
            : 0,
        maxLatencyMs:
          latencies.length > 0
            ? Math.round(Math.max(...latencies) * 100) / 100
            : 0,
        perTxResults,
      };

      setTpsResult(result);
      setTpsProgress("");
      addLog(
        "success",
        `TPS test complete — ${result.tps} tx/s | ${result.totalSucceeded}/${result.totalSent} succeeded | batch duration ${result.durationMs} ms (sign: ${signDuration.toFixed(1)} ms) | amortized ${result.avgLatencyMs} ms/tx`,
      );

      if (result.totalFailed > 0) {
        addLog("error", `${result.totalFailed} transaction(s) failed`);
        const failedSample = failed.slice(0, 10);
        for (const tx of failedSample) {
          addLog("error", `  ↳ ${tx.error ?? "unknown error"}`);
        }
        if (failed.length > 10) {
          addLog("error", `  ↳ ... and ${failed.length - 10} more`);
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog("error", `TPS test failed: ${msg}`);
    } finally {
      setIsTpsRunning(false);
      setTpsProgress("");
    }
  }, [exchangeClient, walletAddress, tpsTxCount, baseURL, resolvePrerequisites, addLog]);

  // --- Onboard handler ---
  const handleOnboard = useCallback(async () => {
    if (!mnemonic || mnemonic.trim() === "") {
      toast.warning("Mnemonic required");
      return;
    }
    addLog("info", "Running onboard flow…");
    try {
      await performOnboard(mnemonic, baseURL);
      addLog("success", `Onboarded. Wallet: ${walletAddress ?? "pending…"}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      addLog("error", `Onboard failed: ${msg}`);
    }
  }, [mnemonic, baseURL, performOnboard, walletAddress, addLog]);

  const isAnyRunning = isLatencyRunning || isTpsRunning || onboardLoading;

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <div style={{ padding: "1rem", maxWidth: 1200 }}>
      <h2>Performance Test</h2>
      <p style={{ color: "#aaa", marginBottom: "1rem" }}>
        Measure end-to-end transaction latency and throughput (TPS) using <code>MsgPlaceOrderRequest</code>.
      </p>

      {/* --- Configuration --- */}
      <div className="perf-test-config-grid">
        <div>
          <label>
            Base URL:
            <input
              type="text"
              value={baseURL}
              onChange={(e) => setBaseURL(e.target.value)}
              className="perf-test-config-input"
              disabled={isAnyRunning}
            />
          </label>
        </div>
        <div>
          <label>
            Mnemonic:
            <input
              type="text"
              value={mnemonic ?? ""}
              onChange={(e) => setOnboardMnemonic(e.target.value)}
              className="perf-test-config-input"
              disabled={isAnyRunning}
            />
          </label>
        </div>
        <div>
          <label>
            Wallet Address:
            <input
              type="text"
              value={walletAddress ?? ""}
              disabled
              className="perf-test-config-input"
              placeholder="automatic (from mnemonic)"
            />
          </label>
          {walletAddress && (
            <span className="perf-test-config-hint success">Connected</span>
          )}
        </div>
        <div>
          <label>
            TPS Burst Count:
            <input
              type="number"
              value={tpsTxCount}
              min={MIN_TPS_TX_COUNT}
              max={MAX_TPS_TX_COUNT}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v)) setTpsTxCount(v);
              }}
              className="perf-test-config-input"
              disabled={isAnyRunning}
            />
          </label>
          <span className="perf-test-config-hint">
            Number of transactions for the TPS burst ({MIN_TPS_TX_COUNT}–{MAX_TPS_TX_COUNT})
          </span>
        </div>
      </div>

      {/* --- Error display --- */}
      {onboardError && (
        <div style={{ color: "red", marginTop: "0.5rem" }}>
          Onboard error: {onboardError}{" "}
          <button onClick={clearOnboardError} style={{ color: "red", background: "transparent", border: "1px solid red", borderRadius: 4, cursor: "pointer", padding: "2px 8px", fontSize: "0.8rem" }}>
            Dismiss
          </button>
        </div>
      )}

      {/* --- Controls --- */}
      <div className="perf-test-controls">
        <button
          onClick={handleOnboard}
          disabled={isAnyRunning || !mnemonic}
          style={{ backgroundColor: "#28a745", color: "#fff" }}
        >
          {onboardLoading ? "Onboarding…" : "Onboard"}
        </button>
        <button
          onClick={runLatencyTest}
          disabled={isAnyRunning || !exchangeReady}
          style={{ backgroundColor: "#007bff", color: "#fff" }}
        >
          {isLatencyRunning ? "Measuring Latency…" : "Run Latency Test"}
        </button>
        <button
          onClick={runTpsTest}
          disabled={isAnyRunning || !exchangeReady}
          style={{ backgroundColor: "#6f42c1", color: "#fff" }}
        >
          {isTpsRunning ? "Measuring TPS…" : "Run TPS Test"}
        </button>
        <button
          onClick={clearLogs}
          disabled={isAnyRunning}
          style={{ backgroundColor: "#dc3545", color: "#fff" }}
        >
          Clear Results
        </button>
      </div>

      {/* --- TPS Progress --- */}
      {tpsProgress && (
        <div className="perf-test-progress">⏳ {tpsProgress}</div>
      )}

      {/* --- Metric Cards --- */}
      {(latencyResult || tpsResult) && (
        <div className="perf-test-metrics">
          {latencyResult && (
            <div className="perf-test-metric-card">
              <div className="perf-test-metric-label">E2E Latency</div>
              <div className={`perf-test-metric-value ${latencyResult.success ? "" : "error"}`}>
                {latencyResult.latencyMs}
                <span className="perf-test-metric-unit">ms</span>
              </div>
              {latencyResult.txhash && (
                <div style={{ fontSize: "0.75rem", color: "#666", marginTop: 4, wordBreak: "break-all" }}>
                  txhash: {latencyResult.txhash}
                </div>
              )}
              {latencyResult.error && (
                <div style={{ fontSize: "0.8rem", color: "#ff6b6b", marginTop: 4 }}>
                  {latencyResult.error}
                </div>
              )}
            </div>
          )}

          {tpsResult && (
            <>
              <div className="perf-test-metric-card">
                <div className="perf-test-metric-label">Throughput</div>
                <div className="perf-test-metric-value">
                  {tpsResult.tps}
                  <span className="perf-test-metric-unit">tx/s</span>
                </div>
              </div>
              <div className="perf-test-metric-card">
                <div className="perf-test-metric-label">Avg Latency</div>
                <div className="perf-test-metric-value">
                  {tpsResult.avgLatencyMs}
                  <span className="perf-test-metric-unit">ms</span>
                </div>
              </div>
              <div className="perf-test-metric-card">
                <div className="perf-test-metric-label">Min / Max Latency</div>
                <div className="perf-test-metric-value" style={{ fontSize: "1.4rem" }}>
                  {tpsResult.minLatencyMs}
                  <span className="perf-test-metric-unit">ms</span>
                  {" / "}
                  {tpsResult.maxLatencyMs}
                  <span className="perf-test-metric-unit">ms</span>
                </div>
              </div>
              <div className="perf-test-metric-card">
                <div className="perf-test-metric-label">Success / Total</div>
                <div className={`perf-test-metric-value ${tpsResult.totalFailed > 0 ? "error" : ""}`}>
                  {tpsResult.totalSucceeded} / {tpsResult.totalSent}
                </div>
              </div>
              <div className="perf-test-metric-card">
                <div className="perf-test-metric-label">Total Duration</div>
                <div className="perf-test-metric-value">
                  {tpsResult.durationMs}
                  <span className="perf-test-metric-unit">ms</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* --- Logs --- */}
      <div className="perf-test-results">
        {logs.length > 0 && (
          <div className="perf-test-section">
            <h3 className="perf-test-section-header">
              <span>Activity Log ({logs.length})</span>
            </h3>
            <div className="perf-test-section-content">
              {logs.map((entry, idx) => (
                <div key={idx} className={`perf-test-log-item ${entry.level}`}>
                  {entry.level === "success" ? "✅" : entry.level === "error" ? "❌" : "ℹ️"}{" "}
                  {entry.message}
                  <span className="perf-test-log-timestamp">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* --- Per-TX Details (TPS test) --- */}
        {tpsResult && tpsResult.perTxResults.length > 0 && (
          <div className="perf-test-section">
            <details>
              <summary className="perf-test-section-header" style={{ listStyle: "none" }}>
                <span>Per-Transaction Details ({tpsResult.perTxResults.length})</span>
                <span className="perf-test-section-chevron">▶</span>
              </summary>
              <div className="perf-test-section-content">
                {tpsResult.perTxResults.map((tx, idx) => (
                  <div
                    key={idx}
                    className={`perf-test-log-item ${tx.success ? "success" : "error"}`}
                  >
                    #{idx + 1} — {tx.latencyMs} ms{" "}
                    {tx.success ? "✅" : `❌ ${tx.error ?? ""}`}
                    {tx.txhash && (
                      <span style={{ fontSize: "0.75rem", color: "#555", marginLeft: 6 }}>
                        tx: {tx.txhash.substring(0, 16)}…
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>

      {logs.length === 0 && !latencyResult && !tpsResult && !isAnyRunning && (
        <div className="perf-test-empty-state">
          Click <strong>Onboard</strong> first, then run a <strong>Latency</strong> or <strong>TPS</strong> test.
        </div>
      )}
    </div>
  );
}

/**
 * Performance Test Page wrapper with SubscriptionProvider.
 */
export default function PerformanceTestPage() {
  return (
    <SubscriptionProvider defaultWsURL={DEFAULT_WS_URL}>
      <PerformanceTestPageContent />
    </SubscriptionProvider>
  );
}
